import type { JsonObject, JsonValue } from "@inf-monkeys-tech/monkeys";
import { Columns3, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  BaseAccordion,
  BaseBadge,
  BaseButton,
  BaseCheckbox,
  BaseField,
  BaseInput,
  BaseInline,
  BaseSelect,
  BaseTabs,
} from "../base";
import type { CapabilityTranslate } from "./contracts";
import { isJsonObject } from "./contracts";
import { DESIGN_CAPABILITY_I18N_KEYS } from "./i18n";

type TagChoice = {
  id: string;
  label: string;
  color?: string;
  groupId?: string;
};

type TagGroupChoice = {
  id: string;
  label: string;
};

export interface MarkerTagCatalog {
  search: string;
  pagination?: JsonObject;
  onSearch(value: string): void;
  onPageChange(page: number): void;
}

type FilterRule = JsonObject & {
  source?: JsonValue;
  meta?: JsonValue;
  whenAllOf?: JsonValue;
  whenAnyOf?: JsonValue;
  requireAllOf?: JsonValue;
  requireAnyOf?: JsonValue;
};

type FilterDraft = JsonObject & {
  allOf?: JsonValue;
  noneOf?: JsonValue;
  anyOf?: JsonValue;
  rules?: JsonValue;
  builtin?: JsonValue;
};

type RequirementMode = "or" | "and" | "and_or";

const markerKeys = DESIGN_CAPABILITY_I18N_KEYS.ontologyManagement;

const text = (value: unknown) =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const stringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value.flatMap((candidate) => {
    const id = text(candidate);
    if (!id || seen.has(id)) return [];
    seen.add(id);
    return [id];
  });
};

const stringGroups = (value: unknown): string[][] =>
  Array.isArray(value)
    ? value.map(stringArray).filter((group) => group.length > 0)
    : [];

const editableStringGroups = (value: unknown): string[][] =>
  Array.isArray(value) ? value.map(stringArray) : [];

const rules = (value: unknown): FilterRule[] =>
  Array.isArray(value)
    ? value.filter(isJsonObject).map((rule) => ({ ...rule }))
    : [];

const markerFilter = (template: JsonObject): FilterDraft => {
  const displayConfig = isJsonObject(template.displayConfig)
    ? template.displayConfig
    : undefined;
  const filter =
    displayConfig && isJsonObject(displayConfig.filterConfigV2)
      ? displayConfig.filterConfigV2
      : undefined;
  return filter ? { ...filter } : {};
};

const hasCreatorCurrentUser = (filter: FilterDraft) => {
  const builtin = isJsonObject(filter.builtin) ? filter.builtin : undefined;
  const creator =
    builtin && isJsonObject(builtin.creatorUserId)
      ? builtin.creatorUserId
      : undefined;
  return creator?.token === "currentUserId";
};

const hasTagFilter = (filter: FilterDraft) =>
  stringArray(filter.allOf).length > 0 ||
  stringArray(filter.noneOf).length > 0 ||
  stringGroups(filter.anyOf).length > 0 ||
  rules(filter.rules).length > 0;

const templateColumns = (template: JsonObject) => {
  const displayConfig = isJsonObject(template.displayConfig)
    ? template.displayConfig
    : undefined;
  const tableColumns =
    displayConfig && isJsonObject(displayConfig.tableColumns)
      ? displayConfig.tableColumns
      : undefined;
  if (!tableColumns) return [];
  return [
    ...stringArray(tableColumns.baseColumns),
    ...stringArray(tableColumns.propertyColumns),
    ...stringArray(tableColumns.featureColumns),
  ];
};

const preferredModeKey = (template: JsonObject) => {
  const displayConfig = isJsonObject(template.displayConfig)
    ? template.displayConfig
    : undefined;
  switch (text(displayConfig?.preferredViewMode)) {
    case "card":
      return markerKeys.viewModeCard;
    case "gallery":
      return markerKeys.viewModeGallery;
    case "trendMatrix":
      return markerKeys.viewModeTrendMatrix;
    default:
      return undefined;
  }
};

const setTemplateFilter = (
  template: JsonObject,
  nextFilter: FilterDraft,
): JsonObject => {
  const displayConfig = isJsonObject(template.displayConfig)
    ? { ...template.displayConfig }
    : {};
  const meaningful = Object.keys(nextFilter).some(
    (key) => key !== "v" && key !== "mode",
  );
  if (meaningful) {
    displayConfig.filterConfigV2 = { v: 1, mode: "tag", ...nextFilter };
  } else {
    delete displayConfig.filterConfigV2;
  }
  if (Object.keys(displayConfig).length > 0) {
    return { ...template, displayConfig };
  }
  const { displayConfig: _ignored, ...withoutDisplayConfig } = template;
  return withoutDisplayConfig;
};

const setCreatorCurrentUser = (
  template: JsonObject,
  selected: boolean,
): JsonObject => {
  const filter = markerFilter(template);
  const builtin = isJsonObject(filter.builtin) ? { ...filter.builtin } : {};
  if (selected) {
    builtin.creatorUserId = { op: "eq", token: "currentUserId" };
  } else {
    delete builtin.creatorUserId;
  }
  if (Object.keys(builtin).length > 0) filter.builtin = builtin;
  else delete filter.builtin;
  return setTemplateFilter(template, filter);
};

const updateFilterList = (
  template: JsonObject,
  key: "allOf" | "noneOf",
  ids: string[],
) => {
  const filter = markerFilter(template);
  if (ids.length > 0) filter[key] = ids;
  else delete filter[key];
  return setTemplateFilter(template, filter);
};

const isSupplementRule = (
  rule: FilterRule,
  groupIndex?: number,
  tagId?: string,
) => {
  if (rule.source !== "or_supplement" || !isJsonObject(rule.meta)) return false;
  if (groupIndex !== undefined && rule.meta.groupIndex !== groupIndex)
    return false;
  if (tagId !== undefined && rule.meta.tagId !== tagId) return false;
  return (
    typeof rule.meta.groupIndex === "number" && Boolean(text(rule.meta.tagId))
  );
};

const supplementRule = (
  filter: FilterDraft,
  groupIndex: number,
  tagId: string,
) =>
  rules(filter.rules).find((rule) => isSupplementRule(rule, groupIndex, tagId));

const requirementMode = (rule: FilterRule | undefined): RequirementMode => {
  const hasAnd = stringArray(rule?.requireAllOf).length > 0;
  const hasOr = stringGroups(rule?.requireAnyOf).length > 0;
  if (hasAnd && hasOr) return "and_or";
  if (hasAnd) return "and";
  return "or";
};

const upsertSupplementRule = (
  template: JsonObject,
  groupIndex: number,
  tagId: string,
  patch: { requireAllOf?: string[]; requireAnyOf?: string[][] },
) => {
  const filter = markerFilter(template);
  const currentRules = rules(filter.rules);
  const currentIndex = currentRules.findIndex((rule) =>
    isSupplementRule(rule, groupIndex, tagId),
  );
  const current = currentIndex >= 0 ? currentRules[currentIndex] : {};
  const next: FilterRule = {
    ...current,
    source: "or_supplement",
    meta: { groupIndex, tagId },
    enabled: current.enabled !== false,
    whenAllOf: [tagId],
    whenAnyOf: [],
    requireAllOf: patch.requireAllOf ?? stringArray(current.requireAllOf),
    requireAnyOf:
      patch.requireAnyOf ?? editableStringGroups(current.requireAnyOf),
  };
  const hasRequirement =
    stringArray(next.requireAllOf).length > 0 ||
    editableStringGroups(next.requireAnyOf).length > 0;
  if (!hasRequirement) {
    filter.rules = currentRules.filter(
      (rule) => !isSupplementRule(rule, groupIndex, tagId),
    );
  } else if (currentIndex >= 0) {
    currentRules[currentIndex] = next;
    filter.rules = currentRules;
  } else {
    filter.rules = [...currentRules, next];
  }
  if (rules(filter.rules).length === 0) delete filter.rules;
  return setTemplateFilter(template, filter);
};

const updateAnyGroup = (
  template: JsonObject,
  groupIndex: number,
  ids: string[],
) => {
  const filter = markerFilter(template);
  const groups = editableStringGroups(filter.anyOf);
  const previous = groups[groupIndex] ?? [];
  const nextGroups = [...groups];
  nextGroups[groupIndex] = ids;
  const removed = previous.filter((id) => !ids.includes(id));
  const nextRules = rules(filter.rules).filter(
    (rule) =>
      !removed.some((tagId) => isSupplementRule(rule, groupIndex, tagId)),
  );
  if (nextGroups.length > 0) filter.anyOf = nextGroups;
  else delete filter.anyOf;
  if (nextRules.length > 0) filter.rules = nextRules;
  else delete filter.rules;
  return setTemplateFilter(template, filter);
};

const removeAnyGroup = (template: JsonObject, groupIndex: number) => {
  const filter = markerFilter(template);
  const groups = stringGroups(filter.anyOf).filter(
    (_group, index) => index !== groupIndex,
  );
  const nextRules = rules(filter.rules).flatMap((rule) => {
    if (!isSupplementRule(rule) || !isJsonObject(rule.meta)) return [rule];
    const currentIndex = Number(rule.meta.groupIndex);
    if (currentIndex === groupIndex) return [];
    if (currentIndex > groupIndex) {
      return [
        { ...rule, meta: { ...rule.meta, groupIndex: currentIndex - 1 } },
      ];
    }
    return [rule];
  });
  if (groups.length > 0) filter.anyOf = groups;
  else delete filter.anyOf;
  if (nextRules.length > 0) filter.rules = nextRules;
  else delete filter.rules;
  return setTemplateFilter(template, filter);
};

const tagChoices = (value: unknown): TagChoice[] =>
  Array.isArray(value)
    ? value.flatMap((choice) => {
        if (!isJsonObject(choice)) return [];
        const id = text(choice.id);
        const label = text(choice.label);
        if (!id || !label) return [];
        return [
          {
            id,
            label,
            ...(text(choice.color) ? { color: text(choice.color) } : {}),
            ...(text(choice.groupId) ? { groupId: text(choice.groupId) } : {}),
          },
        ];
      })
    : [];

const tagGroupChoices = (value: unknown): TagGroupChoice[] =>
  Array.isArray(value)
    ? value.flatMap((choice) => {
        if (!isJsonObject(choice)) return [];
        const id = text(choice.id);
        const label = text(choice.label);
        return id && label ? [{ id, label }] : [];
      })
    : [];

const updateTemplate = (
  templates: JsonObject[],
  index: number,
  patch: JsonObject,
) =>
  templates.map((template, currentIndex) =>
    currentIndex === index ? { ...template, ...patch } : template,
  );

const normalizeTemplateKey = (value: unknown) =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^A-Za-z0-9_.:-]/g, "")
    .slice(0, 64);

export const normalizeOntologyMarkerTemplates = (
  value: unknown,
): JsonObject[] => {
  if (!Array.isArray(value)) {
    throw new TypeError("defaultViewTemplates must be an array.");
  }
  const seen = new Set<string>();
  return value.map((candidate, index) => {
    if (!isJsonObject(candidate)) {
      throw new TypeError("defaultViewTemplates must contain JSON objects.");
    }
    const key = normalizeTemplateKey(candidate.key) || `default-${index + 1}`;
    if (seen.has(key)) {
      throw new TypeError(`Duplicate default view template key: ${key}`);
    }
    seen.add(key);
    const name = text(candidate.name);
    if (!name) {
      throw new TypeError(`Default view template ${key} requires a name.`);
    }
    const sortValue = candidate.sort;
    const parsedSort =
      sortValue === undefined || sortValue === null || sortValue === ""
        ? undefined
        : Number(sortValue);
    if (
      parsedSort !== undefined &&
      !Number.isFinite(parsedSort)
    ) {
      throw new TypeError(`Default view template ${key} sort is invalid.`);
    }
    const normalized: JsonObject = {
      ...candidate,
      key,
      name,
      viewType: text(candidate.viewType) ?? "filter",
    };
    const profile = text(candidate.profile);
    const description = text(candidate.description);
    if (profile) normalized.profile = profile;
    else delete normalized.profile;
    if (description) normalized.description = description;
    else delete normalized.description;
    if (parsedSort === undefined) delete normalized.sort;
    else normalized.sort = Math.trunc(parsedSort);

    if (isJsonObject(candidate.displayConfig)) {
      const displayConfig = { ...candidate.displayConfig };
      if (isJsonObject(displayConfig.filterConfigV2)) {
        const filter = { ...displayConfig.filterConfigV2 };
        const allOf = stringArray(filter.allOf);
        const noneOf = stringArray(filter.noneOf);
        const anyOf = stringGroups(filter.anyOf);
        const normalizedRules = rules(filter.rules).filter(
          (rule) =>
            stringArray(rule.requireAllOf).length > 0 ||
            stringGroups(rule.requireAnyOf).length > 0,
        );
        if (allOf.length > 0) filter.allOf = allOf;
        else delete filter.allOf;
        if (noneOf.length > 0) filter.noneOf = noneOf;
        else delete filter.noneOf;
        if (anyOf.length > 0) filter.anyOf = anyOf;
        else delete filter.anyOf;
        if (normalizedRules.length > 0) filter.rules = normalizedRules;
        else delete filter.rules;
        displayConfig.filterConfigV2 = filter;
      }
      normalized.displayConfig = displayConfig;
    }
    return normalized;
  });
};

const TagSelector = ({
  id,
  choices,
  groups,
  selected,
  disabled,
  translate,
  onChange,
  catalog,
}: {
  id: string;
  choices: TagChoice[];
  groups: TagGroupChoice[];
  selected: string[];
  disabled: boolean;
  translate: CapabilityTranslate;
  onChange: (ids: string[]) => void;
  catalog?: MarkerTagCatalog;
}) => {
  const [search, setSearch] = useState(catalog?.search ?? "");
  const searchTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => { setSearch(catalog?.search ?? ''); }, [catalog?.search]);
  useEffect(() => () => clearTimeout(searchTimer.current), []);
  const [groupId, setGroupId] = useState("");
  const normalizedSearch = search.trim().toLowerCase();
  const declaredIds = new Set(choices.map((choice) => choice.id));
  const available = [
    ...choices,
    ...selected
      .filter((selectedId) => !declaredIds.has(selectedId))
      .map((selectedId): TagChoice => ({ id: selectedId, label: selectedId })),
  ].filter(
    (choice) =>
      (!groupId || choice.groupId === groupId) &&
      (!normalizedSearch ||
        choice.label.toLowerCase().includes(normalizedSearch) ||
        choice.id.toLowerCase().includes(normalizedSearch)),
  );
  return (
    <div className="space-y-2 rounded-md border bg-background p-2">
      <div className="grid gap-2 sm:grid-cols-2">
        <BaseInput
          id={`${id}-search`}
          type="search"
          aria-label={translate(markerKeys.filterSearch)}
          placeholder={translate(markerKeys.filterSearch)}
          value={search}
          disabled={disabled}
          onChange={(event) => {
            const value = event.currentTarget.value;
            setSearch(value);
            clearTimeout(searchTimer.current);
            if (catalog) searchTimer.current = setTimeout(() => catalog.onSearch(value), 250);
          }}
          onKeyDown={event => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            clearTimeout(searchTimer.current);
            catalog?.onSearch(search);
          }}
        />
        {groups.length > 0 ? (
          <BaseSelect
            id={`${id}-group`}
            aria-label={translate(markerKeys.filterTagGroup)}
            value={groupId}
            disabled={disabled}
            options={[
              { value: "", label: translate(markerKeys.filterAllGroups) },
              ...groups.map((group) => ({
                value: group.id,
                label: group.label,
              })),
            ]}
            onChange={(event) => setGroupId(event.currentTarget.value)}
          />
        ) : null}
      </div>
      {available.length > 0 ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {available.map((choice) => (
            <BaseCheckbox
              key={choice.id}
              name=""
              value={choice.id}
              label={choice.label}
              checked={selected.includes(choice.id)}
              disabled={disabled}
              onChange={(event) =>
                onChange(
                  event.currentTarget.checked
                    ? [...selected, choice.id]
                    : selected.filter((candidate) => candidate !== choice.id),
                )
              }
            />
          ))}
        </div>
      ) : (
        <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          {translate(markerKeys.filterNoTags)}
        </div>
      )}
      {catalog?.pagination?.mode === 'window' ? <BaseInline justify="between">
        <BaseButton label={translate('monkeys.design.capability.pagination.previous')} disabled={disabled || catalog.pagination.hasPrevious !== true} onClick={() => catalog.onPageChange(Number(catalog.pagination?.currentPage) - 1)} />
        <BaseButton label={translate('monkeys.design.capability.pagination.next')} disabled={disabled || catalog.pagination.hasNext !== true} onClick={() => catalog.onPageChange(Number(catalog.pagination?.currentPage) + 1)} />
      </BaseInline> : null}
    </div>
  );
};

const MarkerFilterEditor = ({
  id,
  template,
  choices,
  groups,
  disabled,
  translate,
  onChange,
  catalog,
}: {
  id: string;
  template: JsonObject;
  choices: TagChoice[];
  groups: TagGroupChoice[];
  disabled: boolean;
  translate: CapabilityTranslate;
  onChange: (template: JsonObject) => void;
  catalog?: MarkerTagCatalog;
}) => {
  const [section, setSection] = useState("allOf");
  const [activeSupplement, setActiveSupplement] = useState<{
    groupIndex: number;
    tagId: string;
  }>();
  const filter = markerFilter(template);
  const allOf = stringArray(filter.allOf);
  const noneOf = stringArray(filter.noneOf);
  const anyOf = editableStringGroups(filter.anyOf);
  const filterRules = rules(filter.rules);
  const advancedRules = filterRules.filter((rule) => !isSupplementRule(rule));
  const editor = (selected: string[], next: (ids: string[]) => void) => (
    <TagSelector
      catalog={catalog}
      id={id}
      choices={choices}
      groups={groups}
      selected={selected}
      disabled={disabled}
      translate={translate}
      onChange={next}
    />
  );
  const tabs = [
    {
      value: "allOf",
      label: `${translate(markerKeys.filterInclude)}${allOf.length ? ` (${allOf.length})` : ""}`,
      content: editor(allOf, (ids) =>
        onChange(updateFilterList(template, "allOf", ids)),
      ),
    },
    {
      value: "anyOf",
      label: `${translate(markerKeys.filterAny)}${anyOf.length ? ` (${anyOf.length})` : ""}`,
      content: (
        <div className="space-y-3">
          <div className="flex justify-end">
            <BaseButton
              type="button"
              tone="muted"
              size="sm"
              icon={<Plus />}
              label={translate(markerKeys.filterAddOrGroup)}
              disabled={disabled}
              onClick={() => {
                const next = markerFilter(template);
                next.anyOf = [...anyOf, []];
                onChange(setTemplateFilter(template, next));
              }}
            />
          </div>
          {anyOf.length === 0 ? (
            <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
              {translate(markerKeys.filterNoOrGroups)}
            </div>
          ) : (
            anyOf.map((group, groupIndex) => (
              <div key={groupIndex} className="space-y-3 rounded-md border p-3">
                <BaseInline gap="var(--global-spacing-xs)" justify="between">
                  <span className="text-sm font-medium">
                    {translate(markerKeys.filterOrGroup, { n: groupIndex + 1 })}
                  </span>
                  <BaseButton
                    type="button"
                    tone="muted"
                    size="sm"
                    iconOnly
                    icon={<Trash2 />}
                    ariaLabel={translate(markerKeys.filterDeleteGroup)}
                    disabled={disabled}
                    onClick={() =>
                      onChange(removeAnyGroup(template, groupIndex))
                    }
                  />
                </BaseInline>
                {editor(group, (ids) =>
                  onChange(updateAnyGroup(template, groupIndex, ids)),
                )}
                {group.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {group.map((tagId) => {
                      const choice = choices.find(
                        (candidate) => candidate.id === tagId,
                      );
                      return (
                        <BaseButton
                          key={tagId}
                          type="button"
                          tone="muted"
                          size="sm"
                          label={translate(
                            markerKeys.filterConfigureSupplement,
                            {
                              name: choice?.label ?? tagId,
                            },
                          )}
                          disabled={disabled}
                          active={
                            activeSupplement?.groupIndex === groupIndex &&
                            activeSupplement.tagId === tagId
                          }
                          onClick={() =>
                            setActiveSupplement({ groupIndex, tagId })
                          }
                        />
                      );
                    })}
                  </div>
                ) : null}
                {activeSupplement?.groupIndex === groupIndex &&
                group.includes(activeSupplement.tagId) ? (
                  <SupplementEditor
                    catalog={catalog}
                    key={`${groupIndex}:${activeSupplement.tagId}`}
                    id={`${id}-supplement-${groupIndex}-${activeSupplement.tagId}`}
                    template={template}
                    groupIndex={groupIndex}
                    tagId={activeSupplement.tagId}
                    choices={choices}
                    groups={groups}
                    disabled={disabled}
                    translate={translate}
                    onChange={onChange}
                  />
                ) : null}
              </div>
            ))
          )}
          {advancedRules.length > 0 ? (
            <div className="space-y-2 rounded-md border border-dashed p-3 text-sm">
              <div className="font-medium">
                {translate(markerKeys.filterAdvancedRules, {
                  count: advancedRules.length,
                })}
              </div>
              <div className="text-xs text-muted-foreground">
                {translate(markerKeys.filterAdvancedRulesDescription)}
              </div>
              <BaseButton
                type="button"
                tone="muted"
                size="sm"
                label={translate(markerKeys.filterRemoveAdvancedRules)}
                disabled={disabled}
                onClick={() => {
                  const next = markerFilter(template);
                  next.rules = filterRules.filter((rule) =>
                    isSupplementRule(rule),
                  );
                  if (rules(next.rules).length === 0) delete next.rules;
                  onChange(setTemplateFilter(template, next));
                }}
              />
            </div>
          ) : null}
        </div>
      ),
    },
    {
      value: "noneOf",
      label: `${translate(markerKeys.filterExclude)}${noneOf.length ? ` (${noneOf.length})` : ""}`,
      content: editor(noneOf, (ids) =>
        onChange(updateFilterList(template, "noneOf", ids)),
      ),
    },
  ];
  return (
    <BaseTabs
      id={`${id}-sections`}
      value={section}
      items={tabs}
      onValueChange={setSection}
      aria-label={translate(markerKeys.templateTags)}
    />
  );
};

const SupplementEditor = ({
  id,
  template,
  groupIndex,
  tagId,
  choices,
  groups,
  disabled,
  translate,
  onChange,
  catalog,
}: {
  id: string;
  template: JsonObject;
  groupIndex: number;
  tagId: string;
  choices: TagChoice[];
  groups: TagGroupChoice[];
  disabled: boolean;
  translate: CapabilityTranslate;
  onChange: (template: JsonObject) => void;
  catalog?: MarkerTagCatalog;
}) => {
  const filter = markerFilter(template);
  const rule = supplementRule(filter, groupIndex, tagId);
  const mode = requirementMode(rule);
  const requiredAll = stringArray(rule?.requireAllOf);
  const requiredGroups = editableStringGroups(rule?.requireAnyOf);
  const requiredAny = requiredGroups.length === 1 ? requiredGroups[0] : [];
  const [selectedMode, setSelectedMode] = useState<RequirementMode>(mode);
  const applyMode = (nextMode: RequirementMode) => {
    setSelectedMode(nextMode);
    if (nextMode === "and") {
      onChange(
        upsertSupplementRule(template, groupIndex, tagId, {
          requireAllOf: requiredAll,
          requireAnyOf: [],
        }),
      );
    } else if (nextMode === "or") {
      onChange(
        upsertSupplementRule(template, groupIndex, tagId, {
          requireAllOf: [],
          requireAnyOf: requiredAny.length > 0 ? [requiredAny] : [],
        }),
      );
    } else {
      onChange(
        upsertSupplementRule(template, groupIndex, tagId, {
          requireAllOf: requiredAll,
          requireAnyOf: requiredGroups,
        }),
      );
    }
  };
  return (
    <div className="space-y-3 rounded-md border bg-muted/10 p-3">
      <BaseInline gap="var(--global-spacing-xs)" justify="between">
        <span className="text-sm font-medium">
          {translate(markerKeys.filterSupplement, { name: tagId })}
        </span>
        <BaseButton
          type="button"
          tone="muted"
          size="sm"
          label={translate(markerKeys.filterClearSupplement)}
          disabled={disabled}
          onClick={() =>
            onChange(
              upsertSupplementRule(template, groupIndex, tagId, {
                requireAllOf: [],
                requireAnyOf: [],
              }),
            )
          }
        />
      </BaseInline>
      <BaseTabs
        id={`${id}-mode`}
        value={selectedMode}
        aria-label={translate(markerKeys.filterSupplementMode)}
        items={[
          { value: "or", label: translate(markerKeys.filterModeOr) },
          { value: "and", label: translate(markerKeys.filterModeAnd) },
          {
            value: "and_or",
            label: translate(markerKeys.filterModeCombination),
          },
        ]}
        onValueChange={(value) => applyMode(value as RequirementMode)}
      />
      {selectedMode === "and" || selectedMode === "and_or" ? (
        <BaseField label={translate(markerKeys.filterRequiredAll)}>
          <TagSelector
      catalog={catalog}
            id={`${id}-all`}
            choices={choices}
            groups={groups}
            selected={requiredAll}
            disabled={disabled}
            translate={translate}
            onChange={(ids) =>
              onChange(
                upsertSupplementRule(template, groupIndex, tagId, {
                  requireAllOf: ids,
                }),
              )
            }
          />
        </BaseField>
      ) : null}
      {selectedMode === "or" ? (
        <BaseField label={translate(markerKeys.filterRequiredAny)}>
          <TagSelector
      catalog={catalog}
            id={`${id}-any`}
            choices={choices}
            groups={groups}
            selected={requiredAny}
            disabled={disabled}
            translate={translate}
            onChange={(ids) =>
              onChange(
                upsertSupplementRule(template, groupIndex, tagId, {
                  requireAnyOf: ids.length > 0 ? [ids] : [],
                }),
              )
            }
          />
        </BaseField>
      ) : null}
      {selectedMode === "and_or" ? (
        <div className="space-y-3">
          <BaseInline gap="var(--global-spacing-xs)" justify="between">
            <span className="text-sm font-medium">
              {translate(markerKeys.filterRequiredAny)}
            </span>
            <BaseButton
              type="button"
              tone="muted"
              size="sm"
              icon={<Plus />}
              label={translate(markerKeys.filterAddOrGroup)}
              disabled={disabled}
              onClick={() =>
                onChange(
                  upsertSupplementRule(template, groupIndex, tagId, {
                    requireAnyOf: [...requiredGroups, []],
                  }),
                )
              }
            />
          </BaseInline>
          {requiredGroups.map((requiredGroup, index) => (
            <div key={index} className="space-y-2 rounded-md border p-3">
              <BaseInline gap="var(--global-spacing-xs)" justify="between">
                <span className="text-sm font-medium">
                  {translate(markerKeys.filterOrGroup, { n: index + 1 })}
                </span>
                <BaseButton
                  type="button"
                  tone="muted"
                  size="sm"
                  iconOnly
                  icon={<Trash2 />}
                  ariaLabel={translate(markerKeys.filterDeleteGroup)}
                  disabled={disabled}
                  onClick={() =>
                    onChange(
                      upsertSupplementRule(template, groupIndex, tagId, {
                        requireAnyOf: requiredGroups.filter(
                          (_group, groupIndex) => groupIndex !== index,
                        ),
                      }),
                    )
                  }
                />
              </BaseInline>
              <TagSelector
      catalog={catalog}
                id={`${id}-group-${index}`}
                choices={choices}
                groups={groups}
                selected={requiredGroup}
                disabled={disabled}
                translate={translate}
                onChange={(ids) => {
                  const next = [...requiredGroups];
                  next[index] = ids;
                  onChange(
                    upsertSupplementRule(template, groupIndex, tagId, {
                      requireAnyOf: next,
                    }),
                  );
                }}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export const OntologyMarkerEditor = ({
  contextId,
  defaults,
  presets,
  newTemplate,
  translate,
  disabled,
  tagCatalog,
}: {
  contextId: string;
  defaults: JsonObject;
  presets: JsonObject[];
  newTemplate?: JsonObject;
  translate: CapabilityTranslate;
  disabled: boolean;
  tagCatalog?: MarkerTagCatalog;
}) => {
  const initialTemplates = Array.isArray(defaults.defaultViewTemplates)
    ? defaults.defaultViewTemplates.map((template) =>
        isJsonObject(template) ? { ...template } : {},
      )
    : [];
  const [templates, setTemplates] = useState<JsonObject[]>(initialTemplates);
  const [openTemplate, setOpenTemplate] = useState<string>();
  const choices = useMemo(
    () => tagChoices(defaults.tagChoices),
    [defaults.tagChoices],
  );
  const groups = useMemo(
    () => tagGroupChoices(defaults.tagGroupChoices),
    [defaults.tagGroupChoices],
  );
  const id = (name: string) => `ontology-management-${contextId}-${name}`;
  const setTemplate = (index: number, template: JsonObject) =>
    setTemplates((current) =>
      current.map((candidate, currentIndex) =>
        currentIndex === index ? template : candidate,
      ),
    );
  const declaredPresets = presets.flatMap((preset) => {
    const presetId = text(preset.id);
    const labelKey = text(preset.labelKey);
    const presetTemplates = Array.isArray(preset.templates)
      ? preset.templates
          .filter(isJsonObject)
          .map((template) => ({ ...template }))
      : [];
    return presetId && labelKey
      ? [{ id: presetId, labelKey, templates: presetTemplates }]
      : [];
  });
  return (
    <>
      <BaseField
        label={translate(markerKeys.targetOntology)}
        htmlFor={id("target-ontology")}
      >
        <BaseInput
          id={id("target-ontology")}
          value={text(defaults.displayName) ?? ""}
          disabled
          readOnly
        />
      </BaseField>
      <BaseField
        label={translate(markerKeys.label)}
        htmlFor={id("label")}
        required
      >
        <BaseInput
          id={id("label")}
          name="label"
          defaultValue={text(defaults.markerLabel) ?? (text(defaults.markerLabelKey) ? translate(String(defaults.markerLabelKey)) : "")}
          required
          disabled={disabled}
        />
      </BaseField>
      <div className="space-y-3 rounded-md border bg-muted/20 p-3">
        <BaseInline gap="var(--global-spacing-xs)" justify="between">
          <div className="min-w-0">
            <div className="text-sm font-medium">
              {translate(markerKeys.defaultViewTemplates)}
            </div>
            <div className="text-xs text-muted-foreground">
              {translate(markerKeys.defaultViewTemplatesDescription)}
            </div>
          </div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
            {declaredPresets.map((preset) => (
              <BaseButton
                key={preset.id}
                type="button"
                tone="muted"
                size="sm"
                label={translate(preset.labelKey)}
                disabled={disabled}
                onClick={() => setTemplates(preset.templates)}
              />
            ))}
            <BaseButton
              type="button"
              tone="muted"
              size="sm"
              icon={<Plus />}
              label={translate(markerKeys.addTemplate)}
              disabled={disabled}
              onClick={() => {
                const index = templates.length;
                const keyPrefix = text(newTemplate?.keyPrefix) ?? "default-";
                const namePrefix =
                  typeof newTemplate?.namePrefix === "string" &&
                  newTemplate.namePrefix.trim()
                    ? newTemplate.namePrefix
                    : "";
                setTemplates((current) => [
                  ...current,
                  {
                    key: `${keyPrefix}${index + 1}`,
                    name: `${namePrefix}${index + 1}`,
                    viewType: text(newTemplate?.viewType) ?? "filter",
                    sort: index,
                  },
                ]);
                setOpenTemplate(`template-${index}`);
              }}
            />
          </div>
        </BaseInline>
        {templates.length === 0 ? (
          <div className="rounded-md border border-dashed bg-background/60 p-3 text-sm text-muted-foreground">
            {translate(markerKeys.noTemplates)}
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map((template, index) => {
              const filter = markerFilter(template);
              const columns = templateColumns(template);
              const preferred = preferredModeKey(template);
              return (
                <div
                  key={`${text(template.key) ?? "template"}-${index}`}
                  className="overflow-hidden rounded-md border bg-background"
                >
                  <div className="space-y-3 p-3">
                    <BaseInline
                      gap="var(--global-spacing-xs)"
                      justify="between"
                    >
                      <div className="min-w-0 space-y-2">
                        <div>
                          <div className="truncate text-sm font-medium">
                            {text(template.name) ??
                              translate(markerKeys.defaultViewTemplate)}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {text(template.key) ??
                              translate(markerKeys.templateKey)}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {text(template.profile) ? (
                            <BaseBadge
                              tone="muted"
                              label={text(template.profile)}
                            />
                          ) : null}
                          {typeof template.sort === "number" &&
                          template.sort > 0 ? (
                            <BaseBadge
                              tone="muted"
                              label={`${translate(markerKeys.sort)}: ${template.sort}`}
                            />
                          ) : null}
                          {hasCreatorCurrentUser(filter) ? (
                            <BaseBadge
                              tone="muted"
                              label={translate(
                                markerKeys.creatorCurrentUserShort,
                              )}
                            />
                          ) : null}
                          {template.defaultSelected === true ? (
                            <BaseBadge
                              tone="muted"
                              label={translate(markerKeys.defaultSelectedView)}
                            />
                          ) : null}
                          {preferred ? (
                            <BaseBadge
                              tone="muted"
                              label={translate(preferred)}
                            />
                          ) : null}
                          <BaseBadge
                            tone={hasTagFilter(filter) ? "primary" : "muted"}
                            label={translate(
                              hasTagFilter(filter)
                                ? markerKeys.tagFilterEnabled
                                : markerKeys.tagFilterEmpty,
                            )}
                          />
                        </div>
                      </div>
                      <BaseButton
                        type="button"
                        tone="muted"
                        size="sm"
                        iconOnly
                        icon={<Trash2 />}
                        ariaLabel={translate(markerKeys.deleteTemplate)}
                        disabled={disabled}
                        onClick={() =>
                          setTemplates((current) =>
                            current.filter(
                              (_candidate, currentIndex) =>
                                currentIndex !== index,
                            ),
                          )
                        }
                      />
                    </BaseInline>
                    {columns.length > 0 ? (
                      <div className="flex flex-wrap items-center gap-1.5 rounded-md border bg-muted/20 p-2 text-xs text-muted-foreground">
                        <Columns3 className="h-3.5 w-3.5 text-foreground" />
                        <span className="font-medium text-foreground">
                          {translate(markerKeys.tableColumnsSummary)}
                        </span>
                        {columns.slice(0, 5).map((column) => (
                          <span
                            key={column}
                            className="rounded-sm bg-background px-1.5 py-0.5"
                          >
                            {column}
                          </span>
                        ))}
                        {columns.length > 5 ? (
                          <span className="rounded-sm bg-background px-1.5 py-0.5">
                            +{columns.length - 5}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <BaseAccordion
                    value={openTemplate}
                    onValueChange={setOpenTemplate}
                    items={[
                      {
                        value: `template-${index}`,
                        title: (
                          <span className="min-w-0 text-left">
                            <span className="block truncate font-medium">
                              {translate(markerKeys.editTemplateDetails)}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {translate(
                                markerKeys.editTemplateDetailsDescription,
                              )}
                            </span>
                          </span>
                        ),
                        content: (
                          <div className="space-y-3 rounded-md border bg-muted/10 p-3">
                            <div className="grid gap-3 sm:grid-cols-2">
                              <BaseField
                                label={translate(markerKeys.templateKey)}
                                htmlFor={id(`template-${index}-key`)}
                              >
                                <BaseInput
                                  id={id(`template-${index}-key`)}
                                  value={String(template.key ?? "")}
                                  disabled={disabled}
                                  onChange={(event) =>
                                    setTemplates((current) =>
                                      updateTemplate(current, index, {
                                        key: event.currentTarget.value,
                                      }),
                                    )
                                  }
                                />
                              </BaseField>
                              <BaseField
                                label={translate(markerKeys.templateName)}
                                htmlFor={id(`template-${index}-name`)}
                                required
                              >
                                <BaseInput
                                  id={id(`template-${index}-name`)}
                                  value={String(template.name ?? "")}
                                  required
                                  disabled={disabled}
                                  onChange={(event) =>
                                    setTemplates((current) =>
                                      updateTemplate(current, index, {
                                        name: event.currentTarget.value,
                                      }),
                                    )
                                  }
                                />
                              </BaseField>
                              <BaseField
                                label={translate(markerKeys.profile)}
                                htmlFor={id(`template-${index}-profile`)}
                              >
                                <BaseInput
                                  id={id(`template-${index}-profile`)}
                                  value={String(template.profile ?? "")}
                                  disabled={disabled}
                                  onChange={(event) =>
                                    setTemplates((current) =>
                                      updateTemplate(current, index, {
                                        profile: event.currentTarget.value,
                                      }),
                                    )
                                  }
                                />
                              </BaseField>
                              <BaseField
                                label={translate(markerKeys.sort)}
                                htmlFor={id(`template-${index}-sort`)}
                              >
                                <BaseInput
                                  id={id(`template-${index}-sort`)}
                                  inputMode="numeric"
                                  value={String(template.sort ?? "")}
                                  disabled={disabled}
                                  onChange={(event) =>
                                    setTemplates((current) =>
                                      updateTemplate(current, index, {
                                        sort: event.currentTarget.value,
                                      }),
                                    )
                                  }
                                />
                              </BaseField>
                            </div>
                            <BaseField
                              label={translate(markerKeys.templateDescription)}
                              htmlFor={id(`template-${index}-description`)}
                            >
                              <BaseInput
                                id={id(`template-${index}-description`)}
                                value={String(template.description ?? "")}
                                disabled={disabled}
                                onChange={(event) =>
                                  setTemplates((current) =>
                                    updateTemplate(current, index, {
                                      description: event.currentTarget.value,
                                    }),
                                  )
                                }
                              />
                            </BaseField>
                            <BaseCheckbox
                              name=""
                              label={translate(markerKeys.creatorCurrentUser)}
                              checked={hasCreatorCurrentUser(filter)}
                              disabled={disabled}
                              onChange={(event) =>
                                setTemplate(
                                  index,
                                  setCreatorCurrentUser(
                                    template,
                                    event.currentTarget.checked,
                                  ),
                                )
                              }
                            />
                            <div className="space-y-2">
                              <div>
                                <div className="text-sm font-medium">
                                  {translate(markerKeys.templateTags)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {translate(
                                    markerKeys.templateTagsDescription,
                                  )}
                                </div>
                              </div>
                              <MarkerFilterEditor
                                catalog={tagCatalog}
                                id={id(`template-${index}-filter`)}
                                template={template}
                                choices={choices}
                                groups={groups}
                                disabled={disabled}
                                translate={translate}
                                onChange={(next) => setTemplate(index, next)}
                              />
                            </div>
                          </div>
                        ),
                      },
                    ]}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
      <input
        type="hidden"
        name="defaultViewTemplates"
        value={JSON.stringify(templates)}
      />
    </>
  );
};
